import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { applyBrandColors, applyGlobalFont } from "@/lib/company";

const AuthContext = createContext(null);

const MOBILE_MAX = 1024;  // YALNIZCA responsive düzen (menü/kolon davranışı) içindir; YETKİYİ ETKİLEMEZ.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [booting, setBooting] = useState(true);
  const [branding, setBranding] = useState(null);
  const [programLogo, setProgramLogo] = useState("");  // Big BOSS platform logosu (firma logosu yoksa fallback)
  const [features, setFeatures] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [activeSector, setActiveSectorState] = useState(() => { try { return localStorage.getItem("groot_sector") || ""; } catch { return ""; } });
  const setActiveSector = (code) => { setActiveSectorState(code || ""); try { localStorage.setItem("groot_sector", code || ""); } catch (e) {} };
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < MOBILE_MAX : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_MAX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("groot_token");
    if (!token) { setUser(false); setBooting(false); return; }
    if (!localStorage.getItem("groot_session_start")) {
      localStorage.setItem("groot_session_start", new Date().toISOString());
    }
    let cancelled = false;
    const boot = async (attempt = 0) => {
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) { setUser(r.data); setBooting(false); }
      } catch (e) {
        const st = e?.response?.status;
        if (st === 401) {
          // Gerçek oturum kaybı → token temizle, login'e
          if (!cancelled) { localStorage.removeItem("groot_token"); setUser(false); setBooting(false); }
        } else if ((st === 429 || !e?.response || st >= 500) && attempt < 3) {
          // Geçici hata (rate-limit/ağ/5xx) → oturumu DÜŞÜRME; kısa beklemeyle tekrar dene (login'e atma yok)
          await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
          if (!cancelled) boot(attempt + 1);
        } else {
          // Denemeler tükendi ama 401 değil → token'ı KORU (oturumu silme), yalnız login göster
          if (!cancelled) { setUser(false); setBooting(false); }
        }
      }
    };
    boot();
    return () => { cancelled = true; };
  }, []);

  // Platform (Big BOSS) program logosu — public; firma logosu olmayan kiracılarda varsayılan marka.
  useEffect(() => {
    api.get("/platform/program-logo").then((r) => setProgramLogo(r.data?.url || "")).catch(() => {});
  }, []);

  // Belge renk temasını al → localStorage (yazdırılan fiş/fatura buradan okur)
  useEffect(() => {
    if (!user) { try { localStorage.removeItem("groot_company"); localStorage.removeItem("groot_doc_content"); localStorage.removeItem("groot_currency"); } catch (e) {} return; }
    api.get("/settings/doc-theme")
      .then((r) => { if (r.data?.theme) localStorage.setItem("groot_doc_theme", r.data.theme); })
      .catch(() => {});
    // Sistem para birimi → localStorage (tüm tutar biçimlendirmesi buradan okur)
    api.get("/settings/currency")
      .then((r) => { try { localStorage.setItem("groot_currency", r.data?.code || "TRY"); } catch (e) {} })
      .catch(() => {});
    // Tek-kaynak firma bilgisi → localStorage (fiş/fatura/irsaliye/makbuz/etiket buradan okur)
    api.get("/settings/company-profile")
      .then((r) => { try { localStorage.setItem("groot_company", JSON.stringify(r.data || {})); } catch (e) {} })
      .catch(() => {});
    // Belge içeriği şablonları → localStorage (yazdırılan belgeler buradan okur)
    api.get("/settings/doc-templates")
      .then((r) => { try { localStorage.setItem("groot_doc_content", JSON.stringify(r.data || {})); } catch (e) {} })
      .catch(() => {});
  }, [user]);

  // Beyaz-etiket: tenant logosu (varsa header/sidebar bunu gösterir)
  useEffect(() => {
    if (!user) { setBranding(null); try { localStorage.removeItem("groot_branding"); } catch (e) {} return; }
    api.get("/settings/branding").then((r) => { setBranding(r.data); try { localStorage.setItem("groot_branding", JSON.stringify(r.data)); } catch (e) {} }).catch(() => {});
  }, [user]);
  const refreshBranding = async () => {
    try { const { data } = await api.get("/settings/branding"); setBranding(data); try { localStorage.setItem("groot_branding", JSON.stringify(data)); } catch (e) {} return data; } catch (e) { return null; }
  };
  const refreshProgramLogo = async () => {
    try { const { data } = await api.get("/platform/program-logo"); setProgramLogo(data?.url || ""); return data?.url || ""; } catch (e) { return null; }
  };

  // Per-tenant özellik bayrakları (modül aç-kapa + form zorunlulukları) → menü/form buna göre uyarlanır
  useEffect(() => {
    if (!user) { setFeatures(null); return; }
    api.get("/features").then((r) => setFeatures(r.data?.features || {})).catch(() => setFeatures({}));
  }, [user]);
  const feature = (key, fallback = true) => {
    if (!features) return fallback;
    return features[key] !== undefined ? features[key] : fallback;
  };
  const refreshFeatures = async () => {
    try { const { data } = await api.get("/features"); const f = data?.features || {}; setFeatures(f); return f; } catch (e) { return null; }
  };

  // Beyaz-etiket marka renkleri: yalnız yönetici ayarladıysa tüm panele uygula (aksi halde varsayılan görünüm korunur).
  useEffect(() => {
    applyBrandColors(branding?.primary, branding?.accent);
  }, [branding]);

  // Global yazı tipi/boyut/kalınlık (yönetici ayarladıysa tüm arayüze uygulanır).
  useEffect(() => {
    applyGlobalFont(branding);
  }, [branding]);

  // Sektörler: kullanıcının erişebildiği yönetim sektörleri (Yönetici → hepsi) + aktif sektör doğrulaması.
  // refreshSectors: Sektör Ayarları'nda aç/kapa/düzenle sonrası üst bar ve tüm menü ANINDA güncellensin diye.
  const refreshSectors = async () => {
    try {
      const r = await api.get("/sectors");
      const list = (r.data || []).filter((s) => s.is_active);
      setSectors(list);
      const codes = list.map((s) => s.code);
      let cur = "";
      try { cur = localStorage.getItem("groot_sector") || ""; } catch (e) {}
      const next = (cur === "" || codes.includes(cur)) ? cur : (codes[0] || "");
      setActiveSectorState(next);
      try { localStorage.setItem("groot_sector", next); } catch (e) {}
      return list;
    } catch (e) { setSectors([]); return []; }
  };
  useEffect(() => {
    if (!user) { setSectors([]); return; }
    refreshSectors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const applySession = (data) => {
    localStorage.setItem("groot_token", data.token);
    if (data.tenant) { try { localStorage.setItem("groot_tenant", data.tenant); } catch (e) {} }
    localStorage.setItem("groot_session_start", new Date().toISOString());
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password, sector, tenant) => {
    if (tenant) { try { localStorage.setItem("groot_tenant", tenant); } catch (e) {} }
    const { data } = await api.post("/auth/login", { email, password, ...(sector ? { sector } : {}), ...(tenant ? { tenant } : {}) });
    if (data.requires_tenant_selection) {
      return { requiresTenantSelection: true, companies: data.companies || [], email: data.email };
    }
    if (data.twofa_required) {
      if (data.tenant) { try { localStorage.setItem("groot_tenant", data.tenant); } catch (e) {} }
      return { twofaRequired: true, challengeId: data.challenge_id, emailMasked: data.email_masked,
               phoneMasked: data.phone_masked, channel: data.channel, method: data.method, tenant: data.tenant };
    }
    return { user: applySession(data) };
  };

  // Perakende personeli (kasiyer) girişi: Firma (Sunucu) + Kullanıcı Adı + Şifre (bireysel e-posta gerekmez).
  const personnelLogin = async (server, username, password, sector) => {
    try { localStorage.removeItem("groot_tenant"); } catch (e) {}
    const { data } = await api.post("/auth/personnel-login", { server, username, password, ...(sector ? { sector } : {}) });
    if (data.requires_server_selection) {
      return { requiresServerSelection: true, servers: data.servers || [], username: data.username };
    }
    if (data.twofa_required) {
      if (data.tenant) { try { localStorage.setItem("groot_tenant", data.tenant); } catch (e) {} }
      return { twofaRequired: true, challengeId: data.challenge_id, emailMasked: data.email_masked,
               phoneMasked: data.phone_masked, channel: data.channel, method: data.method, tenant: data.tenant };
    }
    return { user: applySession(data) };
  };

  const verify2fa = async (challengeId, code) => {
    const { data } = await api.post("/auth/verify-2fa", { challenge_id: challengeId, code });
    return applySession(data);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  // RBAC canlı senkron: Roller & Yetkiler / rol / lokasyon / kademe değişiklikleri, tekrar giriş GEREKMEDEN
  // tüm açık oturumlara yansısın. Sekmeye dönünce (focus) anında, ayrıca 60 sn'de bir sessizce /auth/me çekilir.
  // Yalnız gerçekten değişiklik varsa state güncellenir (gereksiz yeniden render/istek yok).
  useEffect(() => {
    if (!user) return;
    let stop = false;
    const sync = async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (stop || !data) return;
        setUser((prev) => {
          if (!prev || typeof prev !== "object") return prev;
          const changed =
            JSON.stringify(prev.permissions || []) !== JSON.stringify(data.permissions || []) ||
            prev.role !== data.role || prev.name !== data.name || prev.is_owner !== data.is_owner ||
            JSON.stringify(prev.location_ids || []) !== JSON.stringify(data.location_ids || []) ||
            JSON.stringify(prev.sectors || []) !== JSON.stringify(data.sectors || []);
          return changed ? data : prev;
        });
        refreshFeatures();
      } catch (e) { /* 401 → interceptor yönlendirir */ }
    };
    const onFocus = () => { if (document.visibilityState !== "hidden") sync(); };
    const iv = setInterval(sync, 60000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { stop = true; clearInterval(iv); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("groot_token");
    try { localStorage.removeItem("groot_tenant"); } catch (e) {}
    setUser(false);
  };

  const isPrivileged = !!user && (user.role === "admin" || user.role === "assistant");

  // Yetki YALNIZCA role/verilen izinlere bağlıdır — ekran genişliğine DEĞİL.
  // Yetkili kullanıcı işini web/tablet/PC'de yapabilir (responsive düzen ayrı ele alınır).
  // Sunucu tarafı yetki kontrolü ayrıca korunur; burada gevşetme YAPILMAZ.
  const can = useCallback((perm) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes("*") || user.permissions.includes(perm);
  }, [user]);

  const allowedSectors = sectors;
  const activeSectorObj = sectors.find((s) => s.code === activeSector) || null;

  return (
    <AuthContext.Provider value={{ user, booting, login, personnelLogin, verify2fa, refreshUser, logout, can, isMobile, isPrivileged, branding, refreshBranding, programLogo, refreshProgramLogo, features, feature, refreshFeatures, sectors, allowedSectors, activeSector, setActiveSector, activeSectorObj, refreshSectors }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
